import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Controller, type FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import appConfig from '../../../config';
import type {
  DynamicTableColumnConfig,
  FormConfig,
  InsetVariable,
  ListTotalConfig,
  Question,
} from '../../../config/types';
import content from '../../../content';
import { useFormConfig } from '../../../hooks/useFormConfig';
import { useFormData } from '../../../hooks/useFormData';
import type { FetchFormResponse } from '../../../service/types';
import { Button, Query, Status } from '../../../types';
import {
  getConditionalQuestionDefaultValues,
  getConditionalQuestionNames,
  getConditionalChildRouting,
  hasBlockedSubmitGuardValue,
  isQuestionVisible,
  renderAnswer,
  isPageCompleted,
} from '../../../utils/config-utils';
import { resolveSelectedFormId } from '../../../utils/form-id';
import { getUnsupportedGdadRoleBannerMessage } from '../../../utils/gdad-role-policy';
import { formatLondonIsoDateTime } from '../../../utils/londonTime';
import { parseSafeHtml } from '../../../utils/safe-html';
import { getScsProfessionValidationError } from '../../../utils/scs-profession-validation';
import {
  formatTotalValue,
  getListAnswers,
  getListTotals,
  hasValue,
  interpolateQuestionText,
  interpolateQuestionTextWithAnswerSums,
  objectToString,
  stringToObject,
} from '../../../utils/form-utils';
import type { DynamicTableExcelOptions } from '../../../utils/dynamic-table-excel';
import {
  convertListPageAnswersToDynamicTableValues,
  getDynamicTableListPageExcelOptions,
} from '../../../utils/list-page-excel/dynamic-table-adapter';
import { getInstancePrefix } from '../../../utils/autofill-math';
import ButtonText from '../../ButtonText';
import Notification from '../../Notification/Notification';
import { renderSummaryRow } from '../../SummaryAnswers/SummaryAnswers';
import Tag from '../../Tag/Tag';
import { P } from '../../ui';
import ScsObjectivesDownload from '../../ScsObjectivesDownload/ScsObjectivesDownload';
import AutocompleteWrapper from '../Autocomplete/AutocompleteWrapper';
import AutoFillCondition from '../AutoFillCondition';
import AutoFillMath from '../AutoFillMath';
import BulkTableModal from '../bulk-table/BulkTableModal';
import CheckboxGroup from '../CheckboxGroup/CheckboxGroup';
import { CoreCostDelegatedInput } from '../CoreCostDelegatedInput';
import { CoreCostExternalInput } from '../CoreCostExternalInput';
import { CoreCostSCSInput } from '../CoreCostSCSInput';
import { CoreCostInteralInput } from '../CoreCostInteralInput';
import { CoreCphConsolidatedInput } from '../CoreCphConsolidatedInput';
import { CoreNpsConsolidatedInput } from '../CoreNpsConsolidatedInput';
import { CoreNpsDelegatedInput } from '../CoreNpsDelegatedInput';
import { CoreNpsSCSInput } from '../CoreNpsSCSInput';
import { CoreTtfConsolidatedInput } from '../CoreTtfConsolidatedInput';
import { CoreTthConsolidatedInput } from '../CoreTthConsolidatedInput';
//custom core elements
import { CoreTthDelegatedInput } from '../CoreTthDelegatedInput';
import { CoreTthSCSInput } from '../CoreTthSCSInput';
import { CoreVacConsolidatedInput } from '../CoreVacConsolidatedInput';
import CostInputField from '../CostInputField';
import { DateInputField } from '../DateInputField/DateInputField';
import { DevelopmentProgramme } from '../DevelopmentProgramme';
import { GradeInputField } from '../DateInputField/GradeInputField';
import { GradeInputFieldCoulmnwise } from '../DateInputField/GradeInputFieldCoulmnwise';
import { ShortDateInputField } from '../DateInputField/ShortDateInputField';
import { DisabilityInput } from '../DisabilityInput';
import DynamicGrid from '../DynamicGrid';
import DynamicTable, {
  applyDynamicTableConditions,
  filterDynamicTableRows,
  getDynamicTableFieldName,
  getDynamicTableRowHeaderFieldName,
  normaliseDynamicTableSubmissionValues,
  resolveContinuousDynamicTableConfig,
  resolveDynamicTableRowHeaderOptions,
  resolveDynamicTableRowsFromAnswer,
} from '../DynamicTable';
import DynamicTablesExcelToolbar from '../DynamicTablesExcelToolbar';
import ListPageExcelToolbar from '../ListPageExcelToolbar';
import DualInput from '../DualInput';
import { EthnicityInput } from '../EthnicityInput';
import { ExceptionBreakdownInput } from '../ExceptionBreakdownInput';
import Fieldset from '../Fieldset';
import FileUpload from '../FileUpload';
import { GenderInput } from '../GenderInput';
import { InputBreaches } from '../InputBreaches';
import { InputExternalCompetitions } from '../InputExternalCompetitions';
import { InputExternalExemptions } from '../InputExternalExemptions';
import { InputFairAndOpen } from '../InputFairAndOpen';
import { InputRecruitmentCampaigns } from '../InputRecruitmentCampaigns';
import GdadSkillDefinition from '../GdadSkillDefinition';
import GdadProficiencyRating from '../GdadProficiencyRating';
import Inset from '../Inset';
import InstanceInputField from '../InstanceInputField';
import OptionalLabel from '../OptionalLabel';
import PostcodeLookup, { validateManualAddress } from '../PostcodeLookup/PostcodeLookup';
import RadioGroup from '../RadioGroup/RadioGroup';
import { ReportPeriod } from '../ReportPeriod';
import { ReportMonth } from '../ReportMonth';
import RowsWithInputs from '../RowsWithInputs';
import SelectInputField from '../SelectInputField';
import Textarea from '../Textarea';
import TextInputField from '../TextInputField';
import DataLoad from '../DataLoad';
import AthenaStaffLookup from '../AthenaStaffLookup';
import AthenaNominatorLookup from '../AthenaNominatorLookup';
import GdadRoleSelectionInput from '../GdadRoleSelectionInput';
import TwoTextBox from '../TwoTextBox';
import type { Props } from './types';

enum Screen {
  ListPage = 'ListPage',
  QuestionPage = 'QuestionPage',
  ConfirmDeletePage = 'ConfirmDeletePage',
}

const InProgressWarningBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background-color: rgba(11, 12, 12, 0.65);
`;

const InProgressWarningDialog = styled.div`
  width: min(720px, 100%);
  max-height: min(80vh, 900px);
  overflow: auto;
  background: #ffffff;
  border: 3px solid #0b0c0c;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
`;

const InProgressWarningChangeText = styled.span`
  color: #4c2c92;
  text-decoration: underline;
  text-decoration-thickness: max(1px, 0.0625rem);
  text-underline-offset: 0.1em;
`;

const descriptionContainsHtml = (value: string): boolean => /<\s*\/?\s*[a-z][^>]*>/i.test(value);

const parseCurrencyAmount = (value: unknown): number | null => {
  if (typeof value !== 'string' && typeof value !== 'number') return null;

  const normalized = String(value)
    .replace(/[^\d.]/g, '')
    .trim();
  if (!normalized) return null;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCurrencyAmount = (amount: number): string => {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(rounded);
};

const roundDownToNearestFive = (amount: number): number => Math.floor(amount / 5) * 5;
const roundDownToTwoDecimalPlaces = (amount: number): number =>
  Math.floor((amount + Number.EPSILON) * 100) / 100;

type RewardTeamSplitNotice = {
  message: string;
  note?: string;
};

const getRewardTeamNomineeCount = (
  answers: Record<string, unknown>,
  formConfig: ReturnType<typeof useFormConfig>['formConfig'],
): number => {
  const teamNomineePage = formConfig?.sections
    ?.flatMap((section) => section.subsections)
    .flatMap((subsection) => subsection.pages)
    .find((page) => page.name === 'team-nominee');

  if (
    !teamNomineePage ||
    teamNomineePage.pageType !== 'list' ||
    !teamNomineePage.questions?.length
  ) {
    return 0;
  }

  const groupedTeamAnswers = getListAnswers(answers, teamNomineePage.questions);
  return Object.values(groupedTeamAnswers).filter((group) =>
    group.some(({ answer }) => hasValue(answer)),
  ).length;
};

const getRewardVoucherMinimumAmount = (
  answers: Record<string, unknown>,
  formConfig: ReturnType<typeof useFormConfig>['formConfig'],
): number => {
  const nominationKind = String(answers['nomination-kind'] || '').trim();
  if (nominationKind !== 'Team') return 10;

  const teamNomineeCount = getRewardTeamNomineeCount(answers, formConfig);
  return Math.max(10, teamNomineeCount * 5);
};

const filterRewardVoucherAmountOptions = (
  options: Question['options'],
  minimumAmount: number,
): Question['options'] => {
  if (!Array.isArray(options)) return options;

  return options
    .filter((option) => {
      if (typeof option !== 'string') return true;
      const amount = parseCurrencyAmount(option);
      return amount === null || amount >= minimumAmount;
    })
    .map((option) => {
      if (
        typeof option !== 'string' &&
        'option' in option &&
        option.option === 'Other' &&
        Array.isArray(option.questions)
      ) {
        return {
          ...option,
          questions: option.questions.map((question) =>
            question.name === 'reward-voucher-other-amount'
              ? {
                  ...question,
                  rules: {
                    ...(question.rules || {}),
                    min: minimumAmount,
                  },
                  errorMessages: {
                    ...(question.errorMessages || {}),
                    min: `Voucher amount must be £${minimumAmount} or more`,
                  },
                }
              : question,
          ),
        };
      }

      return option;
    });
};

const prefixQuestionNames = (questions: Question[], prefix: string): Question[] =>
  questions.map((question) => ({
    ...question,
    name: `${prefix}-${question.name}`,
    options: Array.isArray(question.options)
      ? question.options.map((option) => {
          if (
            typeof option !== 'string' &&
            'questions' in option &&
            Array.isArray(option.questions)
          ) {
            return {
              ...option,
              questions: prefixQuestionNames(option.questions, prefix),
            };
          }
          return option;
        })
      : question.options,
  }));

const renderPageDescription = (description: string | undefined): React.ReactNode =>
  (() => {
    const content = description?.trim() || '';
    if (!content) return null;

    // Render HTML descriptions as a single block so multiline tags are not split into visible text.
    if (descriptionContainsHtml(content)) {
      return (
        <div className="question-page-description" data-testid="question-page-description">
          <div className="gdad-description-block">{parseSafeHtml(content)}</div>
        </div>
      );
    }

    const paragraphs = content
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);

    return (
      <div className="question-page-description" data-testid="question-page-description">
        {paragraphs.map((paragraph) => (
          <P key={paragraph.substring(0, 50)}>{parseSafeHtml(paragraph)}</P>
        ))}
      </div>
    );
  })();

export const replaceInsetVariables = (
  content: string | undefined,
  variables: Record<string, InsetVariable> | undefined,
  answers: Record<string, unknown> | undefined,
): string | undefined => {
  if (!content || !variables || !answers) return content;

  let replacedContent = content;
  for (const [placeholder, variable] of Object.entries(variables)) {
    const answerKey = Object.keys(answers).find((key) => key.endsWith(variable.key));
    if (!answerKey) continue;

    const answer = answers[answerKey];
    let replacement = String(answer ?? '');

    if (variable.type === 'multipliedPreviousAnswer') {
      const numericAnswer = Number(replacement.replace(/,/g, ''));
      if (!Number.isFinite(numericAnswer)) continue;

      const total = numericAnswer * variable.multiplier;
      const formattedTotal = new Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2,
      }).format(total);
      replacement = variable.format === 'gbp' ? `£${formattedTotal}` : formattedTotal;
    }

    replacedContent = replacedContent.replace(`\${${placeholder}}`, replacement);
  }

  return replacedContent;
};

type LinkedDynamicTableRemoval = {
  removedItems: string[];
  additionalAnswers: FieldValues;
};

const getSelectedValues = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String);
  if (typeof value !== 'string' || !value.trim()) return [];

  try {
    return getSelectedValues(JSON.parse(value));
  } catch (_) {
    return [value.trim()];
  }
};

export const getLinkedDynamicTableRemoval = (
  formConfig: FormConfig | undefined,
  pageQuestions: Question[],
  savedAnswers: Record<string, unknown>,
  nextAnswers: Record<string, unknown>,
): LinkedDynamicTableRemoval | undefined => {
  const checkboxFields = new Set(
    pageQuestions
      .filter((question) => question.questionType === 'checkbox')
      .map((question) => String(question.name)),
  );
  if (!checkboxFields.size) return undefined;

  const linkedTables =
    formConfig?.sections
      .flatMap((section) => section.subsections)
      .flatMap((subsection) => subsection.pages)
      .flatMap((page) => ('questions' in page ? page.questions || [] : []))
      .filter(
        (question) =>
          question.questionType === 'dynamicTable' &&
          ((question.dynamicTable?.rowFilter &&
            checkboxFields.has(question.dynamicTable.rowFilter.field)) ||
            (question.dynamicTable?.rowHeaderInput?.optionsFrom &&
              checkboxFields.has(question.dynamicTable.rowHeaderInput.optionsFrom.selectionField))),
      ) || [];

  const removedItems = new Set<string>();
  const additionalAnswers: FieldValues = {};

  linkedTables.forEach((question) => {
    const tableConfig = question.dynamicTable;
    if (tableConfig?.rowHeaderInput?.optionsFrom) {
      const previousOptions = new Set(
        resolveDynamicTableRowHeaderOptions(tableConfig, savedAnswers).rowHeaderInput?.options ||
          [],
      );
      const nextOptions = new Set(
        resolveDynamicTableRowHeaderOptions(tableConfig, nextAnswers).rowHeaderInput?.options || [],
      );
      const removedOptions = new Set(
        [...previousOptions].filter((option) => !nextOptions.has(option)),
      );
      if (!removedOptions.size) return;

      const storedValue = savedAnswers[question.name];
      const storedValues = stringToObject(
        typeof storedValue === 'string' ? storedValue : undefined,
      );
      const resolvedConfig = resolveContinuousDynamicTableConfig(tableConfig, storedValues);
      const affectedRows = resolvedConfig.rows.filter(
        (row) => !row.calculation && removedOptions.has(row.label),
      );
      if (!affectedRows.length) return;

      affectedRows.forEach((row) => removedItems.add(row.label));
      const cleanedValues = { ...storedValues };
      affectedRows.forEach((row) => {
        tableConfig.columns.forEach((column) => {
          delete cleanedValues[getDynamicTableFieldName(row.key, column.key)];
        });
        delete cleanedValues[getDynamicTableRowHeaderFieldName(row.key)];
      });
      const cleanedConfig = resolveContinuousDynamicTableConfig(tableConfig, cleanedValues);
      additionalAnswers[question.name] = objectToString(
        applyDynamicTableConditions(cleanedConfig, cleanedValues),
      );
      return;
    }
    if (!tableConfig?.rowFilter) return;

    const filterField = tableConfig.rowFilter.field;
    const previousSelections = new Set(getSelectedValues(savedAnswers[filterField]));
    const nextSelections = new Set(getSelectedValues(nextAnswers[filterField]));
    const untickedSelections = new Set(
      [...previousSelections].filter((selection) => !nextSelections.has(selection)),
    );
    if (!untickedSelections.size) return;

    const storedValue = savedAnswers[question.name];
    const storedValues = stringToObject(typeof storedValue === 'string' ? storedValue : undefined);
    const matchBy = tableConfig.rowFilter.match ?? 'label';
    const affectedRows = tableConfig.rows.filter((row) =>
      untickedSelections.has(matchBy === 'key' ? row.key : row.label),
    );
    const editableColumns = tableConfig.columns.filter(
      (column) => column.enabled !== false && !column.calculation,
    );
    const rowsWithAnswers = affectedRows.filter(
      (row) =>
        row.enabled !== false &&
        !row.calculation &&
        (editableColumns.some((column) =>
          hasValue(storedValues[getDynamicTableFieldName(row.key, column.key)]),
        ) ||
          (tableConfig.continuous &&
            hasValue(storedValues[getDynamicTableRowHeaderFieldName(row.key)]))),
    );
    if (!rowsWithAnswers.length) return;

    rowsWithAnswers.forEach((row) => removedItems.add(row.label));

    const cleanedValues = { ...storedValues };
    affectedRows.forEach((row) => {
      tableConfig.columns.forEach((column) => {
        delete cleanedValues[getDynamicTableFieldName(row.key, column.key)];
      });
      delete cleanedValues[getDynamicTableRowHeaderFieldName(row.key)];
    });

    const recalculatedValues = applyDynamicTableConditions(tableConfig, cleanedValues);
    affectedRows.forEach((row) => {
      tableConfig.columns.forEach((column) => {
        delete recalculatedValues[getDynamicTableFieldName(row.key, column.key)];
      });
      delete recalculatedValues[getDynamicTableRowHeaderFieldName(row.key)];
    });
    additionalAnswers[question.name] = objectToString(recalculatedValues);
  });

  return removedItems.size ? { removedItems: [...removedItems], additionalAnswers } : undefined;
};

export const getQuestionPageResetValues = (
  serverAnswers: FieldValues,
  dynamicTableQuestionNames: string[],
  getCurrentValue: (name: string) => unknown,
  isDirty: (name: string) => boolean,
): FieldValues => {
  if (dynamicTableQuestionNames.length === 0) return serverAnswers;

  const resetValues = { ...serverAnswers };

  dynamicTableQuestionNames.forEach((questionName) => {
    const currentValue = getCurrentValue(questionName);
    const shouldKeepCurrentValue =
      isDirty(questionName) || (!(questionName in serverAnswers) && hasValue(currentValue));
    if (shouldKeepCurrentValue) resetValues[questionName] = currentValue;
  });

  return resetValues;
};

function QuestionPageTemplate({
  config,
  onSave,
  onSaveUnfinished,
  errors,
  disabled,
  onContinue,
  subsectionSlug,
}: Props) {
  const id = useId();
  const { questions = [], pageType } = config;
  const dynamicTableQuestionNamesRef = useRef<string[]>([]);
  dynamicTableQuestionNamesRef.current = questions
    .filter((question) => question.questionType === 'dynamicTable')
    .map((question) => question.name);
  const [showSaveButton, setShowSaveButton] = useState<boolean>(true);
  const [listPageContinueErrorMessage, setListPageContinueErrorMessage] = useState('');
  const [inProgressAnswerCount, setInProgressAnswerCount] = useState(0);
  const [pendingLinkedAnswerDeletion, setPendingLinkedAnswerDeletion] = useState<{
    dataToSave: FieldValues;
    listItemId?: string;
    removedItems: string[];
    additionalAnswers: FieldValues;
  }>();
  const [isDeletingLinkedAnswers, setIsDeletingLinkedAnswers] = useState(false);
  const [screen, setScreen] = useState<string>(
    pageType === 'list' ? Screen.ListPage : Screen.QuestionPage,
  );
  const [currentQuestions, setCurrentQuestions] = useState<Question[]>(questions);
  const { data, isLoading: isFormDataLoading } = useFormData();
  const savedAnswersRef = useRef<Record<string, unknown>>({});
  if (data?.answers) savedAnswersRef.current = { ...data.answers };
  const { tenant, formConfig, enabledSubsections } = useFormConfig();
  const hideSaveUnfinished =
    tenant === 'scs' &&
    ['owner', 'editor'].includes(data?.role || '') &&
    Boolean(subsectionSlug) &&
    formConfig.sections.some((section) =>
      section.quarterReview && section.subsections.some((subsection) =>
        subsection.slug === subsectionSlug && subsection.pages.some((page) => page.name === config.name),
      ),
    ) &&
    isPageCompleted(questions, data?.answers || {}) &&
    !data?.drafts?.[subsectionSlug || '']?.some((draft) => draft.split(':')[0] === config.name);
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedFormId = resolveSelectedFormId(tenant, location?.search || '');
  const [showSecondmentsSummaryNotice, setShowSecondmentsSummaryNotice] = useState(false);

  useEffect(() => {
    setShowSecondmentsSummaryNotice(
      tenant === 'secondments' &&
        ['inward-secondments-summary-page', 'outward-secondments-summary-page'].includes(
          config.name,
        ),
    );
  }, [tenant, config.name, selectedFormId]);

  const methods = useForm<FetchFormResponse['answers']>({
    ...appConfig.form,
    defaultValues: data?.answers,
  });
  const watchedGdadRole = methods.watch('role');
  const selectedGdadRole =
    typeof watchedGdadRole === 'string' && watchedGdadRole.trim().length > 0
      ? watchedGdadRole
      : data?.answers?.role;
  const unsupportedGdadRoleBannerMessage =
    tenant === 'gdad' ? getUnsupportedGdadRoleBannerMessage(selectedGdadRole) : null;

  const listAnswers = data?.answers ?? methods.getValues();
  const listGroupedAnswers = getListAnswers(listAnswers, questions);
  const shouldCheckQueriesContact =
    pageType === 'list' &&
    config.name === 'points-of-contact' &&
    questions.some((q) => q.name === 'contacts-type');
  const hasQueriesContact = Object.values(listGroupedAnswers).some((group) =>
    group.some(({ questionName, answer }) => {
      if (!questionName.endsWith('contacts-type')) return false;
      if (typeof answer === 'string') return answer === 'Point of contact for queries';
      return false;
    }),
  );
  const listPageErrorMessage =
    shouldCheckQueriesContact && !hasQueriesContact
      ? "You must add at least one 'Point of contact for queries'"
      : '';
  const populatedListEntryCount = Object.values(listGroupedAnswers).filter((group) =>
    group.some(({ answer }) => hasValue(answer)),
  ).length;
  const minimumRequiredListItemsErrorMessage =
    pageType === 'list' &&
    config.minimumRequiredListItems &&
    populatedListEntryCount < config.minimumRequiredListItems
      ? config.minimumRequiredListItemsErrorMessage ||
        `You must add at least ${config.minimumRequiredListItems} entries`
      : '';
  const subsectionPages =
    formConfig?.sections
      ?.flatMap((section) => section.subsections)
      .find((subsection) => subsection.slug === subsectionSlug)?.pages || [];
  const enabledSubsectionPages = subsectionSlug
    ? enabledSubsections?.[subsectionSlug]?.pages || subsectionPages
    : subsectionPages;
  const conditionalRouting = getConditionalChildRouting(
    subsectionPages,
    enabledSubsectionPages,
    data || { answers: {}, drafts: {} },
    subsectionSlug || '',
  );
  const listTotalConfig: ListTotalConfig | undefined =
    pageType === 'list' ? config.total : undefined;
  const watchedRewardType = methods.watch('reward-type');
  const watchedRewardVoucherAmount = methods.watch('reward-voucher-amount');
  const watchedRewardVoucherOtherAmount = methods.watch('reward-voucher-other-amount');
  const watchedIybAmount = methods.watch('iyb-amount');
  const watchedScsSharingMethod = methods.watch('objective-sharing-method');
  const watchedScsShareStatus = methods.watch('share-status');
  const isScsObjectivesSharePage =
    tenant === 'scs' && config.name === 'share-objective-with-my-team';
  const isWaitingForScsObjectivesShare =
    isScsObjectivesSharePage &&
    watchedScsSharingMethod === 'Please share them for me' &&
    !String(watchedScsShareStatus || '').trim();
  const currentFormValues = methods.getValues() || {};
  const mergedAnswers: Record<string, unknown> = {
    ...(data?.answers || {}),
    ...currentFormValues,
  };
  const visibleQuestions = currentQuestions.filter((question) =>
    isQuestionVisible(question, mergedAnswers),
  );
  const visibleConditionalQuestions = visibleQuestions.filter(
    (question) =>
      question.questionType !== 'content' &&
      question.questionType !== 'inset' &&
      ((Array.isArray(question.showWhen) && question.showWhen.length > 0) ||
        (Array.isArray(question.disabled) && question.disabled.length > 0)),
  );
  const previousVisibleConditionalQuestions = useRef<Question[]>([]);

  useEffect(() => {
    const visibleQuestionNames = new Set(
      visibleConditionalQuestions.map((question) => question.name),
    );
    const hiddenQuestions = previousVisibleConditionalQuestions.current.filter(
      (question) => typeof question.name === 'string' && !visibleQuestionNames.has(question.name),
    );

    if (hiddenQuestions.length > 0) {
      const resetValues = getConditionalQuestionDefaultValues(hiddenQuestions);
      methods.unregister(getConditionalQuestionNames(hiddenQuestions), {
        keepDefaultValue: true,
      });
      Object.entries(resetValues).forEach(([questionName, resetValue]) => {
        methods.setValue(questionName, resetValue, {
          shouldDirty: true,
          shouldValidate: false,
        });
      });
    }

    previousVisibleConditionalQuestions.current = visibleConditionalQuestions;
  }, [methods, visibleConditionalQuestions]);
  const dynamicTableExcelSheets = visibleQuestions.flatMap((question) => {
    if (question.questionType !== 'dynamicTable' || !question.dynamicTable) return [];
    if (question.dynamicTable.showExcelToolbar === false) return [];
    const storedValues = stringToObject(
      String(data?.answers?.[question.name] ?? methods.getValues(question.name) ?? ''),
    );
    const tableConfig = resolveContinuousDynamicTableConfig(
      resolveDynamicTableRowsFromAnswer(
        resolveDynamicTableRowHeaderOptions(
          filterDynamicTableRows(question.dynamicTable, mergedAnswers),
          mergedAnswers,
        ),
        mergedAnswers,
      ),
      storedValues,
      true,
    );
    const filteredQuestion = { ...question, dynamicTable: tableConfig };

    const buildSheet = (
      title: string,
      columns: DynamicTableColumnConfig[],
    ): { question: Question; options: DynamicTableExcelOptions } => ({
      question: filteredQuestion,
      options: {
        title,
        rowHeader: tableConfig.rowHeader ?? 'Question',
        rows: tableConfig.rows,
        columns,
        values: normaliseDynamicTableSubmissionValues(
          tableConfig,
          storedValues,
          question.rules?.required,
        ),
        interpolationValues: mergedAnswers,
        continuous: tableConfig.continuous,
        rowHeaderInput: tableConfig.rowHeaderInput,
        showRowHeader: tableConfig.showRowHeader,
      },
    });

    if (tableConfig.layout === 'split' && tableConfig.groups?.length) {
      return tableConfig.groups.map((group) =>
        buildSheet(
          group.label,
          group.columnKeys
            .map((key) => tableConfig.columns.find((column) => column.key === key))
            .filter((column): column is DynamicTableColumnConfig => Boolean(column)),
        ),
      );
    }
    return [buildSheet(question.title, tableConfig.columns)];
  });
  const confirmDynamicTableExcelImport = dynamicTableExcelSheets.every(
    ({ question }) => question.dynamicTable?.confirmExcelImport !== false,
  );
  const dynamicTableListPageExcelSheet =
    formConfig.listBulkUpload === true &&
    formConfig.listBulkUploadMode === 'direct' &&
    dynamicTableExcelSheets.length === 1
      ? dynamicTableExcelSheets[0]
      : undefined;
  const dynamicTableListPageExcelOptions = dynamicTableListPageExcelSheet
    ? getDynamicTableListPageExcelOptions(dynamicTableListPageExcelSheet.options, {
        tenant: tenant || formConfig.id || '',
        pageName: dynamicTableListPageExcelSheet.question.name,
      })
    : undefined;
  const rewardVoucherMinimumAmount =
    tenant === 'reward' && config.name === 'reward-details'
      ? getRewardVoucherMinimumAmount(mergedAnswers, formConfig)
      : 10;

  const formErrors = methods.formState.errors;
  const submitCount = methods.formState.submitCount;
  const titleRef = useRef<HTMLHeadingElement>(null);
  const inProgressWarningTitleRef = useRef<HTMLHeadingElement>(null);
  const linkedAnswerDeletionTitleRef = useRef<HTMLHeadingElement>(null);
  const listContinueButtonRef = useRef<HTMLButtonElement>(null);
  const lastAutoScrollSubmitCount = useRef(0);

  const getRewardTeamSplitNotice = (): RewardTeamSplitNotice | null => {
    if (config.name !== 'reward-details') return null;
    const nominationKind = String(mergedAnswers['nomination-kind'] || '').trim();
    if (nominationKind !== 'Team') return null;
    const teamNomineeCount = getRewardTeamNomineeCount(mergedAnswers, formConfig);

    if (teamNomineeCount < 1) return null;

    let rawAmount: unknown;
    let messagePrefix: string | null = null;
    if (watchedRewardType === 'Reward voucher') {
      rawAmount =
        watchedRewardVoucherAmount === 'Other'
          ? watchedRewardVoucherOtherAmount
          : watchedRewardVoucherAmount;
      messagePrefix = 'Each team member will receive a £';
    } else if (watchedRewardType === 'In-year bonus (IYB)') {
      rawAmount = watchedIybAmount;
      messagePrefix = 'Each team member will receive £';
    } else {
      return null;
    }

    const totalAmount = parseCurrencyAmount(rawAmount);
    if (totalAmount === null) return null;

    if (watchedRewardType === 'In-year bonus (IYB)') {
      const perPersonAmount = roundDownToTwoDecimalPlaces(totalAmount / teamNomineeCount);
      return {
        message: `${messagePrefix}${formatCurrencyAmount(perPersonAmount)} (before tax)`,
      };
    }

    const perPersonAmount = roundDownToNearestFive(totalAmount / teamNomineeCount);
    return {
      message: `${messagePrefix}${formatCurrencyAmount(perPersonAmount)} voucher`,
      note: '(Thank you e-vouchers are held in denominations of £5, so the total voucher amount is split per team member with that in mind)',
    };
  };

  useEffect(() => {
    if (tenant !== 'reward' || config.name !== 'reward-details') return;
    if (watchedRewardType !== 'Reward voucher') return;
    if (watchedRewardVoucherAmount === 'Other' || !watchedRewardVoucherAmount) return;

    const selectedAmount = parseCurrencyAmount(watchedRewardVoucherAmount);
    if (selectedAmount !== null && selectedAmount < rewardVoucherMinimumAmount) {
      methods.setValue('reward-voucher-amount', '', {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    config.name,
    methods,
    rewardVoucherMinimumAmount,
    tenant,
    watchedRewardType,
    watchedRewardVoucherAmount,
  ]);

  // set props errors on start
  useEffect(() => {
    for (const error of errors || []) {
      methods.setError(error.name, error.error, error.options);
    }
  }, [errors, methods.setError]);

  useEffect(() => {
    titleRef.current?.focus();
  }, [config]);

  useEffect(() => {
    if (screen === Screen.ListPage) {
      setListPageContinueErrorMessage('');
    }
  }, [screen, data?.answers]);

  useEffect(() => {
    if (inProgressAnswerCount < 1) return;

    inProgressWarningTitleRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setInProgressAnswerCount(0);
      window.requestAnimationFrame(() => listContinueButtonRef.current?.focus());
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [inProgressAnswerCount]);

  useEffect(() => {
    if (!pendingLinkedAnswerDeletion) return;
    linkedAnswerDeletionTitleRef.current?.focus();
  }, [pendingLinkedAnswerDeletion]);

  useEffect(() => {
    if (screen !== Screen.QuestionPage) return;

    const errorKeys = Object.keys(formErrors);

    if (!errorKeys.length) return;
    if (submitCount <= lastAutoScrollSubmitCount.current) return;

    const candidateErrorNames = [
      ...visibleQuestions
        .map((question) => question.name)
        .filter((questionName) => Boolean(formErrors[questionName])),
      ...errorKeys,
    ];
    const uniqueCandidateErrorNames = Array.from(new Set(candidateErrorNames));

    const getTargetForErrorName = (errorName: string): HTMLElement | null => {
      // Prefer scrolling to the fieldset so the question title/legend is visible above the errored control.
      const fieldsetTestId = `question-fieldset-${errorName}`;
      const targetByFieldset = Array.from(
        document.querySelectorAll<HTMLElement>('[data-testid]'),
      ).find((element) => element.getAttribute('data-testid') === fieldsetTestId);
      if (targetByFieldset instanceof HTMLElement) return targetByFieldset;

      const anchorId = `${id}${errorName}`;
      const targetById = document.getElementById(anchorId);
      if (targetById instanceof HTMLElement) return targetById;

      return null;
    };

    let target: HTMLElement | null = null;
    for (const errorName of uniqueCandidateErrorNames) {
      const candidate = getTargetForErrorName(errorName);
      if (candidate) {
        target = candidate;
        break;
      }
    }

    if (!target) {
      const firstVisibleErrorGroup = document.querySelector('.govuk-form-group--error');
      if (firstVisibleErrorGroup instanceof HTMLElement) {
        target = firstVisibleErrorGroup;
      }
    }

    if (target && typeof target.scrollIntoView === 'function') {
      const scrollTarget = target;
      window.requestAnimationFrame(() => {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    lastAutoScrollSubmitCount.current = submitCount;
  }, [formErrors, id, screen, submitCount, visibleQuestions]);

  const addQuestion = useCallback(
    (formValues: FieldValues) => {
      // get max id and increment
      const questionIdPrefix =
        Object.keys(formValues)
          .filter((item) => item.endsWith(questions[0].name))
          .map((item) => Number(item.split('-')[0]))
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      setCurrentQuestions(prefixQuestionNames(questions, String(questionIdPrefix)));
      setScreen(Screen.QuestionPage);
    },
    [questions],
  );

  const getCurrentListItemId = useCallback((): string | undefined => {
    if (pageType !== 'list' || currentQuestions.length === 0) return undefined;

    const firstQuestionName = currentQuestions[0]?.name;
    if (firstQuestionName?.includes('-')) {
      const listItemId = firstQuestionName.split('-')[0];
      console.log(
        '[LIST SAVE] Extracted list item ID:',
        listItemId,
        'from question name:',
        firstQuestionName,
      );
      return listItemId;
    }

    console.warn('[LIST SAVE] Could not extract list item ID. Question name:', firstQuestionName);
    return undefined;
  }, [currentQuestions, pageType]);

  useEffect(() => {
    if (pageType === 'list' && !isFormDataLoading) {
      const formValues = data?.answers || methods.getValues();
      if (Object.entries(getListAnswers(formValues, questions)).length < 1) {
        addQuestion(formValues);
      }
    }
  }, [addQuestion, data?.answers, isFormDataLoading, methods, pageType, questions]);

  // Keep local form values in sync with server data when showing the list view
  useEffect(() => {
    if (data?.answers) {
      if (pageType === 'list' && screen === Screen.ListPage) {
        methods.reset(data.answers);
      }
      if (pageType !== 'list' && screen === Screen.QuestionPage) {
        methods.reset(
          getQuestionPageResetValues(
            data.answers,
            dynamicTableQuestionNamesRef.current,
            (name) => methods.getValues(name),
            (name) => methods.getFieldState(name).isDirty,
          ),
        );
      }
    }
  }, [pageType, screen, data?.answers, methods]);

  async function handleDelete(data: FieldValues, deletedQuestions: Question[]) {
    const deletedQuestionNames = getConditionalQuestionNames(deletedQuestions);
    const dataToSave = Object.fromEntries(
      Object.entries(data).map(([question, answer]) =>
        deletedQuestionNames.includes(question) ? [question, ''] : [question, answer],
      ),
    );

    // update local form state immediately so the list view uses the new values
    methods.reset(dataToSave);

    // Extract list item ID from the first deleted question name (e.g., "1-contact-name" -> "1")
    const listItemId = deletedQuestions[0]?.name?.split('-')[0];

    // Persist the delete first; navigation (0 items to index) is decided in SubsectionScreen
    await onSave(dataToSave, methods.setError, listItemId);

    await queryClient.invalidateQueries({ queryKey: [Query.FormData, selectedFormId || ''] });
    await queryClient.refetchQueries({ queryKey: [Query.FormData, selectedFormId || ''] });

    setScreen(Screen.ListPage);
  }

  function getQuestionTitle(name: string): string {
    return questions.find((question) => question.name === name)?.title || name;
  }

  function renderTotals(formValues: FieldValues) {
    if (!listTotalConfig || !listTotalConfig.columns?.length) return null;

    const { groupBy, rows, hasEntries } = getListTotals(formValues, questions, listTotalConfig);
    if (!hasEntries) return null;

    return (
      <>
        <h2 className="govuk-heading-m">Totals</h2>
        <table className="govuk-table">
          <thead className="govuk-table__head">
            <tr className="govuk-table__row">
              {groupBy.map((fieldName) => (
                <th className="govuk-table__header" key={`group-header-${fieldName}`}>
                  {getQuestionTitle(fieldName)}
                </th>
              ))}
              {listTotalConfig.columns.map((column) => (
                <th className="govuk-table__header" key={`total-header-${column.field}`}>
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="govuk-table__body">
            {rows.map((row) => (
              <tr className="govuk-table__row" key={`totals-row-${row.key}`}>
                {groupBy.map((fieldName, index) => (
                  <td className="govuk-table__cell" key={`group-value-${row.key}-${fieldName}`}>
                    {row.groupValues[index]}
                  </td>
                ))}
                {row.totals.map((value, index) => (
                  <td className="govuk-table__cell" key={`total-value-${row.key}-${index}`}>
                    {formatTotalValue(
                      value,
                      questions.find(
                        (question) => question.name === listTotalConfig.columns[index].field,
                      ),
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  }

  function getListItemStatus(
    itemId: string,
    answers: ReturnType<typeof getListAnswers>[string],
  ): Status {
    // Check if this specific list item is marked as draft
    // Draft identifiers are in format "page-name:list-item-id" (e.g., "contact-details:1")
    const draftIdentifier = `${config.name}:${itemId}`;
    const isDraft =
      (subsectionSlug && data?.drafts?.[subsectionSlug]?.includes(draftIdentifier)) || false;

    // Completed iff every required question in this entry has a value.
    const isComplete = answers.every(({ question, answer }) =>
      question?.rules?.required === true ? hasValue(answer) : true,
    );

    // Draft status takes precedence over completion status
    return isDraft ? Status.InProgress : isComplete ? Status.Completed : Status.InProgress;
  }

  function getInProgressAnswerCount(formValues: FieldValues): number {
    return Object.entries(getListAnswers(formValues, questions)).filter(
      ([itemId, answers]) => getListItemStatus(itemId, answers) === Status.InProgress,
    ).length;
  }

  // Per-entry status + incremental label (Answer 1/2/3...)
  function renderListSummary(formValues: FieldValues, questions: Question[], listLabel?: string) {
    const groupedAnswers = getListAnswers(formValues, questions);

    console.log('[renderListSummary] Full drafts data:', data?.drafts);
    console.log(
      '[renderListSummary] Drafts for subsection:',
      subsectionSlug,
      '->',
      data?.drafts?.[subsectionSlug || ''],
    );

    const listItemLabel = listLabel || 'Response';

    return (
      <>
        {Object.entries(groupedAnswers).map(([id, answers], idx) => {
          const pageName = config.name;
          const draftIdentifier = `${pageName}:${id}`;

          console.log(
            `[renderListSummary] Checking item ${id}:`,
            'draftIdentifier:',
            draftIdentifier,
            'subsectionSlug:',
            subsectionSlug,
          );

          const isDraft =
            (subsectionSlug && data?.drafts?.[subsectionSlug]?.includes(draftIdentifier)) || false;

          console.log(`[renderListSummary] Item ${id} isDraft:`, isDraft);

          const status = getListItemStatus(id, answers);

          return (
            <React.Fragment key={id}>
              <tr className="govuk-table__row">
                <th className="govuk-table__cell" style={{ whiteSpace: 'nowrap' }}>
                  <span>
                    {listItemLabel} {idx + 1}
                  </span>
                  <Tag status={status} style={{ marginLeft: 8 }} />
                </th>
                <td
                  className="govuk-table__cell"
                  colSpan={3}
                  style={{ height: '46px', textAlign: 'right' }}
                >
                  <ButtonText
                    type="button"
                    data-in-progress-change={status === Status.InProgress ? 'true' : undefined}
                    style={{ fontSize: '19px', marginRight: '20px' }}
                    onClick={() => {
                      setListPageContinueErrorMessage('');
                      setCurrentQuestions(prefixQuestionNames(questions, id));
                      setScreen(Screen.QuestionPage);
                    }}
                  >
                    {content.buttonChange}
                  </ButtonText>
                  <ButtonText
                    type="button"
                    style={{ fontSize: '19px' }}
                    onClick={() => {
                      setListPageContinueErrorMessage('');
                      setCurrentQuestions(prefixQuestionNames(questions, id));
                      setScreen(Screen.ConfirmDeletePage);
                    }}
                  >
                    {content.buttonRemove}
                  </ButtonText>
                </td>
              </tr>
              {answers.map((answer) => {
                const formattedAnswers = renderAnswer(answer.question, answer.answer);
                return (
                  <React.Fragment key={answer.questionName}>
                    {renderSummaryRow(answer.question, formattedAnswers, formValues)}
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          );
        })}
      </>
    );
  }

  const listSummaryValues = data?.answers ?? methods.getValues();
  const handleSaveAndAddAnother = methods.handleSubmit(async (formValues) => {
    const listItemId = getCurrentListItemId();
    const saveResult = await onSave(formValues, methods.setError, listItemId, {
      mode: 'addAnother',
    });
    if (saveResult === false) return;

    methods.clearErrors();
    setListPageContinueErrorMessage('');
    addQuestion(methods.getValues());
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  });

  return (
    <>
      {showSecondmentsSummaryNotice && (
        <BulkTableModal
          title="Secondment counts"
          onClose={() => setShowSecondmentsSummaryNotice(false)}
        >
          <p className="govuk-body">
            Counts are populated from the details table and can be changed.
          </p>
          <button
            type="button"
            className="govuk-button"
            onClick={() => setShowSecondmentsSummaryNotice(false)}
          >
            Close
          </button>
        </BulkTableModal>
      )}
      {unsupportedGdadRoleBannerMessage && (
        <Notification
          content={unsupportedGdadRoleBannerMessage}
          className="govuk-notification-banner--error"
        />
      )}
      {pageType === 'list' &&
        screen !== Screen.ConfirmDeletePage &&
        formConfig.listBulkUpload === true && (
          <ListPageExcelToolbar
            title={config.title}
            pageName={config.name}
            questions={questions}
            answers={listSummaryValues}
            tenant={tenant || formConfig.id || ''}
            disabled={disabled}
            ready={!isFormDataLoading}
            autoOpenSelector={screen === Screen.ListPage}
            autoOpenTable={location.state?.showListSummary !== true}
            mode={formConfig.listBulkUploadMode}
            onImport={async (importedAnswers) => {
              const saveResult = await onSave(importedAnswers, methods.setError);
              if (saveResult === false) {
                throw new Error('The spreadsheet could not be saved.');
              }
            }}
            onSaveUnfinished={(importedAnswers) => onSaveUnfinished(importedAnswers)}
          />
        )}
      {screen === Screen.ListPage && (
        <FormProvider {...methods}>
          {(listPageContinueErrorMessage || listPageErrorMessage) && (
            <div className="govuk-form-group govuk-form-group--error">
              <p className="govuk-error-message" id={`${id}points-of-contact-queries`}>
                {listPageContinueErrorMessage || listPageErrorMessage}
              </p>
            </div>
          )}
          {renderPageDescription(config.description)}
          {renderTotals(listSummaryValues)}
          <table className="govuk-table">
            <tbody className="govuk-table__body">
              {renderListSummary(listSummaryValues, questions, config.listLabel)}
            </tbody>
          </table>

          <div className="gov-uk-button-group">
            {!config.hideContinueButton && (
              <button
                className="govuk-button save-button"
                type="button"
                name="add"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => {
                  setListPageContinueErrorMessage('');
                  addQuestion(methods.getValues());
                }}
                disabled={disabled}
              >
                {content.buttonAddAnother}
              </button>
            )}
            {config.changeTypeButton && conditionalRouting.controllerPageName && (
              <button
                className="govuk-button govuk-button--secondary"
                style={{ marginRight: '15px' }}
                type="button"
                name="change-type"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => {
                  setListPageContinueErrorMessage('');
                  navigate(`../${conditionalRouting.controllerPageName}${location.search || ''}`);
                }}
                disabled={disabled}
              >
                Change type
              </button>
            )}
            <button
              ref={listContinueButtonRef}
              className="govuk-button govuk-button--secondary"
              type="button"
              name="continue"
              data-module="govuk-button"
              data-prevent-double-click="true"
              onClick={() => {
                const nextErrorMessage =
                  listPageErrorMessage || minimumRequiredListItemsErrorMessage;
                if (nextErrorMessage) {
                  setListPageContinueErrorMessage(nextErrorMessage);
                  return;
                }
                setListPageContinueErrorMessage('');
                const nextInProgressAnswerCount = getInProgressAnswerCount(listSummaryValues);
                if (nextInProgressAnswerCount > 0) {
                  setInProgressAnswerCount(nextInProgressAnswerCount);
                  return;
                }
                if (onContinue) onContinue();
              }}
              disabled={disabled}
            >
              {content.buttonContinue}
            </button>
          </div>
        </FormProvider>
      )}

      {inProgressAnswerCount > 0 && (
        <InProgressWarningBackdrop>
          <InProgressWarningDialog
            className="govuk-!-padding-6"
            role="dialog"
            aria-modal="true"
            aria-labelledby="in-progress-answers-title"
          >
            <h2
              ref={inProgressWarningTitleRef}
              id="in-progress-answers-title"
              className="govuk-heading-m govuk-!-margin-bottom-4"
              tabIndex={-1}
            >
              Incomplete answers
            </h2>
            <p className="govuk-body">
              {inProgressAnswerCount} answers are currently saved as{' '}
              <Tag
                status={Status.InProgress}
                style={{
                  display: 'inline-block',
                  lineHeight: 1,
                  marginLeft: 4,
                  padding: '1px 4px',
                  verticalAlign: 'middle',
                }}
              />
              . You will be unable to submit your form until these answers are{' '}
              <strong
                className="app-task-list__tag govuk-tag govuk-tag--green"
                style={{
                  display: 'inline-block',
                  lineHeight: 1,
                  marginLeft: 4,
                  padding: '1px 4px',
                  verticalAlign: 'middle',
                }}
              >
                Complete
              </strong>
              .
            </p>
            <p className="govuk-body govuk-!-margin-bottom-6">
              To complete a question, click the{' '}
              <InProgressWarningChangeText>Change</InProgressWarningChangeText> link next to the{' '}
              <Tag
                status={Status.InProgress}
                style={{
                  display: 'inline-block',
                  lineHeight: 1,
                  padding: '1px 4px',
                  verticalAlign: 'middle',
                }}
              />{' '}
              label, then save the question using the Save and Continue button.
            </p>
            <div className="govuk-button-group govuk-!-margin-bottom-0">
              <button
                type="button"
                className="govuk-button"
                data-module="govuk-button"
                onClick={() => {
                  setInProgressAnswerCount(0);
                  window.requestAnimationFrame(() => {
                    document
                      .querySelector<HTMLButtonElement>('[data-in-progress-change="true"]')
                      ?.focus();
                  });
                }}
              >
                Back to questions
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                data-module="govuk-button"
                onClick={() => {
                  setInProgressAnswerCount(0);
                  if (onContinue) onContinue();
                }}
              >
                Save as incomplete
              </button>
            </div>
          </InProgressWarningDialog>
        </InProgressWarningBackdrop>
      )}

      {screen === Screen.ConfirmDeletePage && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit((data) => handleDelete(data, currentQuestions))}>
            <P>{content.confirmRemoval}</P>

            {currentQuestions.map((question) => (
              <P key={question.name}>
                <b>{question.title}</b>: {methods.getValues(question.name)}
              </P>
            ))}

            <div className="gov-uk-button-group">
              <button
                className="govuk-button govuk-button--warning delete-button"
                type="submit"
                name="delete"
                data-module="govuk-button"
                data-prevent-double-click="true"
                disabled={disabled}
              >
                {content.buttonConfirmRemove}
              </button>
              <button
                className="govuk-button govuk-button--secondary"
                type="button"
                name="cancel"
                data-module="govuk-button"
                data-prevent-double-click="true"
                onClick={() => setScreen(Screen.ListPage)}
                disabled={disabled}
              >
                {content.buttonCancelRemove}
              </button>
            </div>
          </form>
        </FormProvider>
      )}

      {screen === Screen.QuestionPage && (
        <>
          {renderPageDescription(config.description)}
          {tenant === 'gdad' && config.name === 'proficiency-rating' && (
            <GdadProficiencyRating answers={data?.answers || methods.getValues()} />
          )}
          {config.gdadSkillDefinition && (
            <GdadSkillDefinition
              skillName={config.gdadSkillDefinition.skillName}
              skillUrl={config.gdadSkillDefinition.skillUrl}
              level={config.gdadSkillDefinition.level}
              bullets={config.gdadSkillDefinition.bullets}
            />
          )}

          <FormProvider {...methods}>
            <form
              //onSubmit={methods.handleSubmit(async (data) => {
              //  await onSave(data, methods.setError);
              //  if (pageType === 'list') setScreen(Screen.ListPage);
              //})}
              onSubmit={methods.handleSubmit(async (data) => {
                if (tenant === 'scs' && config.name === 'person-details') {
                  const professionError = getScsProfessionValidationError(data);
                  if (professionError) {
                    methods.setError('person-details-profession-secondary', {
                      type: 'validate',
                      message: professionError,
                    });
                    return;
                  }
                  methods.clearErrors('person-details-profession-secondary');
                }

                const listItemId = getCurrentListItemId();
                let dataToSave = data;
                if (
                  isScsObjectivesSharePage &&
                  data['objective-sharing-method'] === 'I will share them myself'
                ) {
                  const existingSharedAt = String(methods.getValues('share-status') || '').trim();
                  const sharedAt = existingSharedAt || formatLondonIsoDateTime();
                  if (!existingSharedAt) {
                    methods.setValue('share-status', sharedAt, {
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }
                  dataToSave = { ...data, 'share-status': sharedAt };
                }

                const linkedTableRemoval = getLinkedDynamicTableRemoval(
                  formConfig,
                  questions,
                  savedAnswersRef.current,
                  dataToSave,
                );
                if (linkedTableRemoval) {
                  setPendingLinkedAnswerDeletion({
                    dataToSave,
                    listItemId,
                    ...linkedTableRemoval,
                  });
                  return;
                }

                await onSave(dataToSave, methods.setError, listItemId);
                if (pageType === 'list') return;
              })}
            >
              {tenant === 'scs' && config.name === 'share-objective-with-my-team' && (
                <ScsObjectivesDownload
                  answers={mergedAnswers}
                  formId={selectedFormId || data?.formId}
                  formVersion={data?.formVersion ?? data?.form_version}
                />
              )}
              {dynamicTableListPageExcelSheet && dynamicTableListPageExcelOptions ? (
                <ListPageExcelToolbar
                  {...dynamicTableListPageExcelOptions}
                  disabled={disabled}
                  mode="direct"
                  confirmImport={false}
                  getAnswers={() => {
                    const { options, question } = dynamicTableListPageExcelSheet;
                    return getDynamicTableListPageExcelOptions(
                      {
                        ...options,
                        values: stringToObject(methods.getValues(question.name)),
                      },
                      {
                        tenant: tenant || formConfig.id || '',
                        pageName: question.name,
                      },
                    ).answers;
                  }}
                  onImport={async (importedAnswers) => {
                    const { question } = dynamicTableListPageExcelSheet;
                    if (!question.dynamicTable) return;
                    const importedValues = convertListPageAnswersToDynamicTableValues(
                      question.dynamicTable,
                      importedAnswers,
                    );
                    const updatedValues = applyDynamicTableConditions(
                      question.dynamicTable,
                      importedValues,
                    );
                    const value = objectToString(
                      normaliseDynamicTableSubmissionValues(
                        question.dynamicTable,
                        updatedValues,
                        question.rules?.required,
                      ),
                    );
                    methods.setValue(question.name, value, {
                      shouldDirty: true,
                      shouldTouch: true,
                      shouldValidate: true,
                    });
                    await onSaveUnfinished(
                      { ...methods.getValues(), [question.name]: value },
                      undefined,
                      { stayOnPage: true },
                    );
                  }}
                />
              ) : dynamicTableExcelSheets.length > 0 ? (
                <DynamicTablesExcelToolbar
                  title={config.title}
                  tables={dynamicTableExcelSheets.map(({ options }) => options)}
                  disabled={disabled}
                  confirmImport={confirmDynamicTableExcelImport}
                  onImport={async (importedSheets) => {
                    const importedByQuestion = new Map<string, Record<string, string>>();
                    dynamicTableExcelSheets.forEach(({ question }, index) => {
                      const current = importedByQuestion.get(question.name) ?? {};
                      importedByQuestion.set(question.name, {
                        ...current,
                        ...(importedSheets[index] ?? {}),
                      });
                    });

                    const importedAnswers: Record<string, string> = {};
                    dynamicTableExcelSheets.forEach(({ question }) => {
                      if (importedAnswers[question.name] !== undefined || !question.dynamicTable)
                        return;
                      const importedValues = importedByQuestion.get(question.name) ?? {};
                      const updatedValues = applyDynamicTableConditions(question.dynamicTable, {
                        ...stringToObject(methods.getValues(question.name)),
                        ...importedValues,
                      });
                      const value = objectToString(
                        normaliseDynamicTableSubmissionValues(
                          question.dynamicTable,
                          updatedValues,
                          question.rules?.required,
                        ),
                      );
                      importedAnswers[question.name] = value;
                      methods.setValue(question.name, value, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    });

                    await onSaveUnfinished(
                      { ...methods.getValues(), ...importedAnswers },
                      undefined,
                      { stayOnPage: true },
                    );
                  }}
                />
              ) : null}
              {visibleQuestions.map((question) => {
                const errorMessage = methods.formState.errors[question.name];
                const anchorId = `${id}${question.name}`;
                const legendId = `${anchorId}-legend`;
                const defaultValue = methods.getValues(question.name);
                // DynamicTable subscribes to its own value; avoid rerendering the whole page for each cell keystroke.
                const watchedValue =
                  question.questionType === 'dynamicTable'
                    ? defaultValue
                    : methods.watch(question.name);
                const resolvedQuestionTitle =
                  interpolateQuestionTextWithAnswerSums(question.title, question, mergedAnswers) ||
                  question.title;
                const hintText = interpolateQuestionText(question.hint, mergedAnswers);
                const resolvedQuestion = {
                  ...question,
                  title: resolvedQuestionTitle,
                  hint: hintText,
                  dynamicTable: question.dynamicTable
                    ? resolveDynamicTableRowsFromAnswer(
                        resolveDynamicTableRowHeaderOptions(
                          filterDynamicTableRows(question.dynamicTable, mergedAnswers),
                          mergedAnswers,
                        ),
                        mergedAnswers,
                      )
                    : undefined,
                };
                const prepopulateValue = data?.prepopulatedPlaceholders?.[question.name];
                const placeholderOverride =
                  prepopulateValue !== undefined &&
                  prepopulateValue !== null &&
                  String(prepopulateValue).length > 0
                    ? String(prepopulateValue)
                    : question.placeholder;
                const isRewardVoucherAmountQuestion =
                  tenant === 'reward' &&
                  config.name === 'reward-details' &&
                  question.name === 'reward-voucher-amount';
                const rules = question?.rules || {};
                const effectiveOptions = isRewardVoucherAmountQuestion
                  ? filterRewardVoucherAmountOptions(question.options, rewardVoucherMinimumAmount)
                  : question.options;
                const effectiveErrorMessages = question.errorMessages;
                const isAutoMath = !!question.autofillMath;
                const isAutoCondition = !!question.autoFillCondition;
                const hideOptional = !!question.hideOptionalLabel;
                const showRequiredAsterisk =
                  formConfig?.requiredFlag === true && rules?.required === true;
                const rewardTeamSplitNotice =
                  question.name === 'nomination-justification-text'
                    ? getRewardTeamSplitNotice()
                    : null;

                const submitGuardMessage =
                  typeof question.submitGuard?.message === 'string' &&
                  question.submitGuard.message.trim().length > 0 &&
                  hasBlockedSubmitGuardValue(question, {
                    ...(methods.getValues() as any),
                    [question.name]: watchedValue,
                  } as any)
                    ? question.submitGuard.message
                    : null;
                const nomineeFieldPrefix = getInstancePrefix(question.name);
                const nomineeLookupValue = methods.watch(`${nomineeFieldPrefix}nominee-lookup`);
                const isRewardAutofillNomineeField =
                  tenant === 'reward' &&
                  question.readOnly === true &&
                  [
                    'nominee-full-name',
                    'nominee-email',
                    'nominee-job-title',
                    'nominee-staff-number',
                    'nominee-cost-centre',
                    'nominee-grade',
                    'nominee-function',
                    'nominee-line-manager-email',
                  ].some((suffix) => question.name.endsWith(suffix));
                const hasRewardAutofillValue =
                  isRewardAutofillNomineeField &&
                  typeof nomineeLookupValue === 'string' &&
                  nomineeLookupValue.trim().length > 0 &&
                  typeof watchedValue === 'string' &&
                  watchedValue.trim().length > 0;
                if (question.questionType === 'hidden') {
                  return (
                    <input key={question.name} type="hidden" {...methods.register(question.name)} />
                  );
                }

                if (question.questionType === 'content') {
                  return (
                    <div className="govuk-form-group" key={question.name || question.title}>
                      {resolvedQuestion.title ? (
                        <h2 className="govuk-heading-m">{parseSafeHtml(resolvedQuestion.title)}</h2>
                      ) : null}
                      {renderPageDescription(resolvedQuestion.hint)}
                      {renderPageDescription(question.description)}
                    </div>
                  );
                }

                return (
                  <div
                    className={`govuk-form-group ${errorMessage && 'govuk-form-group--error'}`}
                    key={question.name}
                    style={
                      hasRewardAutofillValue
                        ? {
                            borderLeft: '5px solid #00703c',
                            paddingLeft: '15px',
                          }
                        : undefined
                    }
                  >
                    <Fieldset
                      title={
                        <OptionalLabel
                          text={resolvedQuestionTitle}
                          optional={!(rules?.required === true || hideOptional)}
                        />
                      }
                      showRequiredAsterisk={showRequiredAsterisk}
                      hintText={hintText}
                      legendId={legendId}
                      data-testid={`question-fieldset-${question.name}`}
                    >
                      <>
                        {rewardTeamSplitNotice && (
                          <>
                            <p
                              className={`govuk-body govuk-!-font-weight-bold ${
                                rewardTeamSplitNotice.note
                                  ? 'govuk-!-margin-bottom-1'
                                  : 'govuk-!-margin-bottom-3'
                              }`}
                            >
                              {rewardTeamSplitNotice.message}
                            </p>
                            {rewardTeamSplitNotice.note && (
                              <p className="govuk-body govuk-!-margin-top-0 govuk-!-margin-bottom-3">
                                {rewardTeamSplitNotice.note}
                              </p>
                            )}
                            <hr className="govuk-section-break govuk-section-break--m govuk-section-break--visible" />
                          </>
                        )}
                        {question.autofillMath && (
                          <AutoFillMath question={question} answers={data?.answers} />
                        )}
                        {question.autoFillCondition && (
                          <AutoFillCondition question={question} answers={data?.answers} />
                        )}
                        {question.questionType === 'radio' && (
                          <RadioGroup
                            anchorId={anchorId}
                            disabled={disabled || question.readOnly || isAutoCondition}
                            question={question}
                            id={id}
                            showRequiredFlag={formConfig?.requiredFlag}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            assets={data?.assets}
                          />
                        )}
                        {question.questionType === 'date' && (
                          <DateInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'shortDate' && (
                          <ShortDateInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {/* {question.questionType === 'gradeInput' && (
                          <GradeInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )} */}
                        {question.questionType === 'twoTextBox' && (
                          <TwoTextBox
                            title1={question.title}
                            title2="arul-title"
                            name1={question.name}
                            name2={`${question.name}hello`}
                            width="one-quarter"
                            rules1={rules}
                            rules2={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages1={question.errorMessages}
                            errorMessages2={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {/* {question.questionType === 'grid' && <Grid5x5 />} */}
                        {question.questionType === 'select' && (
                          <>
                            <div
                              className={`govuk-form-group ${
                                errorMessage && 'govuk-form-group--error'
                              }`}
                              key={question.name}
                              id={anchorId}
                            >
                              <SelectInputField
                                name={question.name}
                                id={anchorId}
                                aria-labelledby={legendId}
                                hintOption={question.hintOption}
                                options={effectiveOptions || []}
                                assets={data?.assets}
                                disabled={disabled || question.readOnly || isAutoCondition}
                                rules={rules}
                                title={question.title}
                                errorMessages={effectiveErrorMessages}
                                detailsHeading={question.detailsHeading}
                                detailsContent={question.detailsContent}
                                showRequiredFlag={formConfig?.requiredFlag}
                                defaultValue={
                                  typeof question.defaultValue === 'string'
                                    ? question.defaultValue
                                    : undefined
                                }
                                scsObjectivesShare={
                                  tenant === 'scs' && config.name === 'share-objective-with-my-team'
                                    ? {
                                        answers: mergedAnswers,
                                        formId: selectedFormId || data?.formId,
                                        formVersion: data?.formVersion ?? data?.form_version,
                                      }
                                    : undefined
                                }
                              />
                            </div>
                            {isScsObjectivesSharePage &&
                              question.name === 'objective-sharing-method' &&
                              watchedScsSharingMethod === 'I will share them myself' && (
                                <P>
                                  You can download your objectives using the download button above
                                  to share them with your team.
                                </P>
                              )}
                          </>
                        )}
                        {question.questionType === 'checkbox' && (
                          <CheckboxGroup
                            anchorId={anchorId}
                            disabled={disabled}
                            question={resolvedQuestion}
                            id={id}
                            showRequiredFlag={formConfig?.requiredFlag}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            assets={data?.assets}
                          />
                        )}
                        {question.questionType === 'textInput' && (
                          <TextInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            approver={question.approver}
                            allowSelfApprover={question.allowSelfApprover}
                            restrictDomains={question.restrictDomains}
                            placeholder={placeholderOverride}
                            width={question.width}
                            wordCount={question.wordCount}
                            characterCount={question.characterCount}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={effectiveErrorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            autoFill={question.autoFill}
                            userEmail={question.userEmail}
                            userNameFromEmail={question.userNameFromEmail}
                            readOnly={question.readOnly || isAutoMath || isAutoCondition}
                            aria-readonly={question.readOnly || isAutoMath || isAutoCondition}
                          />
                        )}
                        {question.questionType === 'textInputSmall' && (
                          <TextInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            approver={question.approver}
                            allowSelfApprover={question.allowSelfApprover}
                            restrictDomains={question.restrictDomains}
                            placeholder={placeholderOverride}
                            wordCount={question.wordCount}
                            characterCount={question.characterCount}
                            rules={rules}
                            width="one-third"
                            defaultValue={defaultValue || ''}
                            errorMessages={effectiveErrorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            autoFill={question.autoFill}
                            userEmail={question.userEmail}
                            userNameFromEmail={question.userNameFromEmail}
                            readOnly={question.readOnly || isAutoMath || isAutoCondition}
                            aria-readonly={question.readOnly || isAutoMath || isAutoCondition}
                          />
                        )}
                        {question.questionType === 'inset' && (
                          <Inset
                            insetContent={replaceInsetVariables(
                              question.insetContent,
                              question.insetVariables,
                              data?.answers,
                            )}
                            heading={'my heading'}
                          />
                        )}
                        {question.questionType === 'rowsWithInput' && (
                          <RowsWithInputs
                            aria-labelledby={legendId}
                            id={anchorId}
                            // tableData={question.tableData || { headers: [], rows: [] }}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'dualInput' && (
                          <DualInput
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            name1="elan-name"
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'grade' && (
                          <GradeInputField
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'gradeColumn' && (
                          <GradeInputFieldCoulmnwise
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'recruitmentCampaignInput' && (
                          <InputRecruitmentCampaigns
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'ethnicityInput' && (
                          <EthnicityInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'genderInput' && (
                          <GenderInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'disabilityInput' && (
                          <DisabilityInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'exceptionBreakdownInput' && (
                          <ExceptionBreakdownInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'fairAndOpenInput' && (
                          <InputFairAndOpen
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'breachesInput' && (
                          <InputBreaches
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'externalExemptionInput' && (
                          <InputExternalExemptions
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'internalCompetitionsInput' && (
                          <InputExternalCompetitions
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'textarea' && (
                          <Textarea
                            aria-labelledby={legendId}
                            numeric={!!question.rules?.number || undefined}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            placeholder={placeholderOverride}
                            wordCount={question.wordCount}
                            characterCount={question.characterCount}
                            textRows={question.textRows}
                            emailList={question.emailList}
                            rules={rules}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'reportPeriod' && (
                          <ReportPeriod
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'reportMonth' && (
                          <ReportMonth
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'developmentProgramme' && (
                          <DevelopmentProgramme
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'dataLoad' && question.lookupQuestion && (
                          <DataLoad
                            name={question.name}
                            title={question.title}
                            lookupQuestion={question.lookupQuestion}
                            subsectionSlug={subsectionSlug}
                            pageName={config.name}
                            successMessage={question.successMessage}
                          />
                        )}
                        {question.questionType === 'gdadRoleSelectionInput' && (
                          <GdadRoleSelectionInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'athenaStaffLookup' && (
                          <AthenaStaffLookup
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'athenaNominatorLookup' && (
                          <AthenaNominatorLookup
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'addressLookup' && (
                          <Controller
                            name={question.name}
                            control={methods.control}
                            rules={{ validate: validateManualAddress }}
                            render={({ field: { ref: _ref, ...field }, fieldState: { error } }) => (
                              <PostcodeLookup
                                {...field}
                                onValueSelect={(data) => methods.setValue(question.name, data)}
                                onValueChange={methods.setValue}
                                setShowSaveButton={setShowSaveButton}
                                defaultValue={defaultValue}
                                manualAddressError={error}
                                detailsHeading={question.detailsHeading}
                                detailsContent={question.detailsContent}
                              />
                            )}
                          />
                        )}
                        {question.questionType === 'autoComplete' && (
                          <AutocompleteWrapper
                            id={anchorId}
                            items={question.items || []}
                            options={Array.isArray(question.options) ? question.options : []}
                            name={question.name}
                            title={question.title}
                            rules={rules}
                            matchLimit={question.matchLimit}
                            fuzzyMatch={question.fuzzyMatch}
                            placeholder={question.searchPlaceholder || placeholderOverride}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            showRequiredFlag={formConfig?.requiredFlag}
                          />
                        )}
                        {question.questionType === 'fileUpload' && (
                          <FileUpload
                            id={anchorId}
                            name={question.name}
                            label={question.label}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'costInput' && (
                          <CostInputField
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            txt={question.txt || ''}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'instanceInput' && (
                          <InstanceInputField
                            question={question}
                            aria-labelledby={legendId}
                            id={anchorId}
                            disabled={disabled}
                            title={question.title}
                            name={question.name}
                            rules={rules}
                            txt={question.txt || ''}
                            defaultValue={defaultValue || ''}
                            errorMessages={question.errorMessages}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            readOnly={isAutoMath}
                            aria-readonly={isAutoMath}
                          />
                        )}
                        {question.questionType === 'coreTthDelegatedInput' && (
                          <CoreTthDelegatedInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreTthSCSInput' && (
                          <CoreTthSCSInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostDelegatedInput' && (
                          <CoreCostDelegatedInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostInteralInput' && (
                          <CoreCostInteralInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostExternalInput' && (
                          <CoreCostExternalInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreCostSCSInput' && (
                          <CoreCostSCSInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreNpsDelegatedInput' && (
                          <CoreNpsDelegatedInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'coreNpsSCSInput' && (
                          <CoreNpsSCSInput
                            question={question}
                            anchorId={anchorId}
                            disabled={disabled}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}
                        {question.questionType === 'CoreNpsConsolidatedInput' && (
                          <CoreNpsConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreTthConsolidatedInput' && (
                          <CoreTthConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreTtfConsolidatedInput' && (
                          <CoreTtfConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreCphConsolidatedInput' && (
                          <CoreCphConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'CoreVacConsolidatedInput' && (
                          <CoreVacConsolidatedInput
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'dynamicGrid' && (
                          <DynamicGrid
                            question={question}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                          />
                        )}

                        {question.questionType === 'dynamicTable' && (
                          <DynamicTable
                            question={resolvedQuestion}
                            disabled={disabled}
                            anchorId={anchorId}
                            detailsHeading={question.detailsHeading}
                            detailsContent={question.detailsContent}
                            interpolationValues={mergedAnswers}
                            showExcelToolbar={false}
                          />
                        )}

                        {/* Submit guards block submission, but we still allow save-and-continue.
                            Show the guard message inline when a blocked value is selected. */}
                        {submitGuardMessage && (
                          <p
                            className="govuk-error-message govuk-!-margin-top-2 govuk-!-margin-bottom-0"
                            data-testid={`submit-guard-${question.name}`}
                          >
                            {submitGuardMessage}
                          </p>
                        )}
                      </>
                    </Fieldset>
                  </div>
                );
              })}

              <div className="gov-uk-button-group">
                {showSaveButton && (
                  <button
                    className="govuk-button save-button"
                    type="submit"
                    name={Button.Save}
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    disabled={disabled || isWaitingForScsObjectivesShare}
                  >
                    {content.buttonSave}
                  </button>
                )}
                {pageType === 'list' && config.addButton && (
                  <button
                    className="govuk-button save-add-another-button"
                    type="button"
                    name="save-and-add-another"
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    onClick={handleSaveAndAddAnother}
                    disabled={disabled}
                  >
                    {content.buttonSaveAddAnother}
                  </button>
                )}
                {!hideSaveUnfinished && (
                  <button
                    className="govuk-button govuk-button--secondary"
                    type="button"
                    name={Button.SaveUnfinished}
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    onClick={() => {
                      methods.clearErrors();
                      // Extract list item ID from question names for list pages
                      let listItemId: string | undefined;
                      if (pageType === 'list' && currentQuestions.length > 0) {
                        const firstQuestionName = currentQuestions[0]?.name;
                        if (firstQuestionName?.includes('-')) {
                          listItemId = firstQuestionName.split('-')[0];
                          console.log(
                            '[LIST DRAFT] Extracted list item ID:',
                            listItemId,
                            'from question name:',
                            firstQuestionName,
                          );
                        } else {
                          console.warn(
                            '[LIST DRAFT] Could not extract list item ID. Question name:',
                            firstQuestionName,
                          );
                        }
                      } else {
                        console.log(
                          '[DRAFT] Not a list page or no questions. PageType:',
                          pageType,
                          'Questions:',
                          currentQuestions.length,
                        );
                      }
                      onSaveUnfinished(methods.getValues(), listItemId);
                    }}
                    disabled={
                      disabled ||
                      (tenant === 'scs' &&
                        config.name === 'share-objective-with-my-team' &&
                        Boolean(String(watchedScsShareStatus || '').trim()))
                    }
                  >
                    {content.buttonSaveUnfinished}
                  </button>
                )}
                {config.changeTypeButton && conditionalRouting.controllerPageName && (
                  <button
                    className="govuk-button govuk-button--secondary"
                    style={{ marginLeft: '15px' }}
                    type="button"
                    name="change-type"
                    data-module="govuk-button"
                    data-prevent-double-click="true"
                    onClick={() => {
                      methods.clearErrors();
                      navigate(
                        `../${conditionalRouting.controllerPageName}${location.search || ''}`,
                      );
                    }}
                    disabled={disabled}
                  >
                    Change type
                  </button>
                )}
              </div>
            </form>
          </FormProvider>

          {pendingLinkedAnswerDeletion && (
            <InProgressWarningBackdrop>
              <InProgressWarningDialog
                className="govuk-!-padding-6"
                role="dialog"
                aria-modal="true"
                aria-labelledby="linked-answer-deletion-title"
              >
                <h2
                  ref={linkedAnswerDeletionTitleRef}
                  id="linked-answer-deletion-title"
                  className="govuk-heading-m govuk-!-margin-bottom-4"
                  tabIndex={-1}
                >
                  You have unticked the below items
                </h2>
                <ul className="govuk-list govuk-list--bullet">
                  {pendingLinkedAnswerDeletion.removedItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="govuk-body">
                  If you continue, any previous answers on this form linked to them will be deleted.
                </p>
                <div className="govuk-button-group govuk-!-margin-bottom-0">
                  <button
                    type="button"
                    className="govuk-button govuk-button--secondary"
                    data-module="govuk-button"
                    disabled={isDeletingLinkedAnswers}
                    onClick={() => setPendingLinkedAnswerDeletion(undefined)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="govuk-button govuk-button--warning"
                    data-module="govuk-button"
                    disabled={isDeletingLinkedAnswers}
                    onClick={async () => {
                      setIsDeletingLinkedAnswers(true);
                      try {
                        const saved = await onSave(
                          pendingLinkedAnswerDeletion.dataToSave,
                          methods.setError,
                          pendingLinkedAnswerDeletion.listItemId,
                          {
                            additionalAnswers: pendingLinkedAnswerDeletion.additionalAnswers,
                          },
                        );
                        if (saved !== false) setPendingLinkedAnswerDeletion(undefined);
                      } finally {
                        setIsDeletingLinkedAnswers(false);
                      }
                    }}
                  >
                    Continue
                  </button>
                </div>
              </InProgressWarningDialog>
            </InProgressWarningBackdrop>
          )}
        </>
      )}
    </>
  );
}

export default QuestionPageTemplate;
